import { apiRequest, ApiRequestError } from '@/lib/api-client';
import { captureError } from '@/lib/monitoring';
import { grantState } from './grant';
import { pinCryptoAvailable } from './pin';
import { OfflineStore } from './store';
import {
  pdaSyncPullSchema,
  pdaSyncPushResultSchema,
  type PdaOfflineGrant,
  type PdaSyncPull,
} from '@fuel-system/contracts';
import { isNetworkFailure, pullInto, pushOutbox, type SyncTransport } from './sync';
import { generateDeviceCode, newUuid } from './temp-reference';
import type { DeviceIdentity } from './types';

/**
 * The device side of the PDA offline mode (fuel-pda.md section 13) as one
 * module-level runtime: the IndexedDB store, the connection state, the
 * outbox counters and the sync loop. React reads it through
 * `useOfflineState()`; nothing here runs until `startOfflineRuntime()`.
 */

const BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api/v1';
/** How often the outbox is sent while operations wait (section 13.7). */
export const SYNC_INTERVAL_MS = 60_000;
/** How often the server is asked again while it cannot be reached. */
export const HEALTH_RETRY_MS = 15_000;
const HEALTH_TIMEOUT_MS = 5_000;
/** Operations older than this raise the home warning (section 13.7). */
export const STALE_PENDING_MS = 24 * 60 * 60 * 1000;

export interface OfflineSnapshot {
  /** IndexedDB and WebCrypto exist: offline work is possible on this device. */
  available: boolean;
  /** The server answered the last request or health check. */
  online: boolean;
  pendingCount: number;
  /** `enteredAt` of the oldest waiting operation. */
  oldestPendingAt: string | null;
  syncing: boolean;
  /** The server has the sync endpoints (an older API answers 404). */
  syncSupported: boolean;
  /** The user whose PIN unlocked the app, or who signed in online in this run. */
  unlockedUserId: string | null;
  /** Bumped after every sync so screens reading the store reload. */
  revision: number;
}

let snapshot: OfflineSnapshot = {
  available: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  pendingCount: 0,
  oldestPendingAt: null,
  syncing: false,
  syncSupported: true,
  unlockedUserId: null,
  revision: 0,
};
const listeners = new Set<() => void>();
const reconnectListeners = new Set<() => void>();

function update(change: Partial<OfflineSnapshot>): void {
  snapshot = { ...snapshot, ...change };
  for (const listener of listeners) listener();
}

export function subscribeOffline(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getOfflineSnapshot(): OfflineSnapshot {
  return snapshot;
}

/** Called when the server is reachable again (the session is read again then). */
export function onReconnect(listener: () => void): () => void {
  reconnectListeners.add(listener);
  return () => reconnectListeners.delete(listener);
}

// --- store -------------------------------------------------------------------

let storePromise: Promise<OfflineStore | null> | null = null;

function storageAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && pinCryptoAvailable();
}

/** The device store, or null where IndexedDB or WebCrypto is missing (offline mode off). */
export function getOfflineStore(): Promise<OfflineStore | null> {
  if (!storePromise) {
    storePromise = storageAvailable()
      ? OfflineStore.open(indexedDB).catch((error: unknown) => {
          captureError(error, { area: 'offline-store' });
          return null;
        })
      : Promise.resolve(null);
    void storePromise.then((store) => update({ available: store !== null }));
  }
  return storePromise;
}

export async function deviceIdentity(store: OfflineStore): Promise<DeviceIdentity> {
  return store.device(() => generateDeviceCode(), newUuid);
}

/** The cached grant when it still allows offline work. */
export async function validGrant(): Promise<PdaOfflineGrant | null> {
  const store = await getOfflineStore();
  const grant = (await store?.getValue('grant')) ?? null;
  const serverTime = (await store?.getValue('serverTime')) ?? null;
  return grantState(grant, Date.now(), serverTime) === 'VALID' ? grant : null;
}

/** Whether a request that failed on the network may be served from the device. */
export async function canWorkOffline(): Promise<boolean> {
  const grant = await validGrant();
  return grant !== null && grant.userId === snapshot.unlockedUserId;
}

export function setUnlockedUser(userId: string | null): void {
  if (snapshot.unlockedUserId !== userId) update({ unlockedUserId: userId });
}

export async function refreshCounts(): Promise<void> {
  const store = await getOfflineStore();
  if (!store) return;
  const operations = await store.outbox();
  const oldest = operations.reduce<string | null>(
    (current, operation) =>
      current === null || operation.enteredAt < current ? operation.enteredAt : current,
    null,
  );
  update({
    pendingCount: operations.length,
    oldestPendingAt: oldest,
    revision: snapshot.revision + 1,
  });
}

// --- connection --------------------------------------------------------------------

let healthTimer: ReturnType<typeof setTimeout> | undefined;

function setOnline(online: boolean): void {
  if (snapshot.online === online) return;
  update({ online });
  if (online) {
    for (const listener of reconnectListeners) listener();
    void syncNow();
  }
}

/** A request got no answer: show غير متصل and keep asking the server. */
export function reportNetworkFailure(): void {
  setOnline(false);
  scheduleHealthCheck();
}

/** A request got an answer from the API. */
export function reportServerAnswer(): void {
  if (healthTimer !== undefined) {
    clearTimeout(healthTimer);
    healthTimer = undefined;
  }
  setOnline(true);
}

function scheduleHealthCheck(): void {
  if (healthTimer !== undefined) return;
  healthTimer = setTimeout(() => {
    healthTimer = undefined;
    void checkHealth();
  }, HEALTH_RETRY_MS);
}

/** A light request to the API's liveness probe (section 13.3). */
export async function checkHealth(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    const response = await fetch(`${BASE_URL}/health/live`, {
      cache: 'no-store',
      credentials: 'include',
      signal: controller.signal,
    });
    if (response.ok) {
      reportServerAnswer();
      return true;
    }
  } catch {
    // Unreachable; handled below.
  } finally {
    clearTimeout(timer);
  }
  reportNetworkFailure();
  return false;
}

// --- sync --------------------------------------------------------------------------

export const httpTransport: SyncTransport = {
  push: (input) =>
    apiRequest('/pda/sync/push', {
      method: 'POST',
      body: input,
      schema: pdaSyncPushResultSchema,
    }),
  pull: (since, deviceId) => {
    const query = new URLSearchParams({ deviceId });
    if (since) query.set('since', since);
    return apiRequest(`/pda/sync/pull?${query.toString()}`, { schema: pdaSyncPullSchema });
  },
};

let transport: SyncTransport = httpTransport;
/** The branch of the current online session: pulls happen only with one. */
let onlineBranchId: string | null = null;
let running: Promise<void> | null = null;
const pullListeners = new Set<(pulled: PdaSyncPull) => void>();

/** Tests replace the HTTP transport. */
export function setSyncTransport(next: SyncTransport): void {
  transport = next;
}

export function setOnlineBranch(branchId: string | null): void {
  onlineBranchId = branchId;
}

export function onPull(listener: (pulled: PdaSyncPull) => void): () => void {
  pullListeners.add(listener);
  return () => pullListeners.delete(listener);
}

async function runSync(): Promise<void> {
  const store = await getOfflineStore();
  if (!store) return;
  update({ syncing: true });
  try {
    // One install id for every push and pull: the server binds the grant to it.
    const device = await deviceIdentity(store);
    if ((await store.outboxCount()) > 0) {
      await pushOutbox(store, transport, device.id);
    }
    if (onlineBranchId && snapshot.syncSupported) {
      const pulled = await pullInto(store, transport, onlineBranchId, device.id);
      for (const listener of pullListeners) listener(pulled);
    }
    reportServerAnswer();
  } catch (error) {
    if (isNetworkFailure(error)) reportNetworkFailure();
    else if (error instanceof ApiRequestError && error.status === 404) {
      // An API without the offline endpoints: online work goes on as before.
      update({ syncSupported: false });
    } else if (!(error instanceof ApiRequestError && error.status < 500)) {
      captureError(error, { area: 'offline-sync' });
    }
  } finally {
    await refreshCounts();
    update({ syncing: false });
  }
}

let lastSyncAt = 0;

/** Syncs unless a sync ran in the last minute: the session read starts one at most that often. */
export function syncIfStale(now: number = Date.now()): Promise<void> {
  if (now - lastSyncAt < SYNC_INTERVAL_MS) return running ?? Promise.resolve();
  return syncNow();
}

/**
 * Push then pull (section 13.7). Runs once at a time; a call while a run is
 * in flight waits for that run.
 */
export function syncNow(): Promise<void> {
  lastSyncAt = Date.now();
  running ??= runSync().finally(() => {
    running = null;
  });
  return running;
}

/**
 * What the minute timer does: online, sync (the pull keeps the pending list
 * and the revocations fresh even with an empty outbox); offline with waiting
 * operations, ask the server again.
 */
export function minuteAction(
  state: Pick<OfflineSnapshot, 'online' | 'pendingCount'>,
): 'sync' | 'health' | 'none' {
  if (state.online) return 'sync';
  return state.pendingCount > 0 ? 'health' : 'none';
}

let started = false;

/**
 * Starts the loop once: sync when the network returns and every minute
 * (`minuteAction`), and asks the browser to keep the device data.
 */
export function startOfflineRuntime(): () => void {
  if (started || typeof window === 'undefined') return () => undefined;
  started = true;
  void getOfflineStore().then(() => refreshCounts());
  void navigator.storage?.persist?.().catch(() => false);

  const onOnline = (): void => void checkHealth();
  const onOffline = (): void => reportNetworkFailure();
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  const interval = setInterval(() => {
    const action = minuteAction(snapshot);
    if (action === 'sync') void syncNow();
    else if (action === 'health') void checkHealth();
  }, SYNC_INTERVAL_MS);

  return () => {
    started = false;
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    clearInterval(interval);
  };
}
